import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  editSiteGroupCurfew,
  loadSiteGroup,
  loadSiteGroupCurfew,
  loadSiteGroupCurfewForSiteGroup,
  loadSiteGroupCurfews,
  loadSiteGroupWeatherConfigurationForSiteGroup,
} from '@sophocles/features/siteGroups/api';
import { SiteGroupCurfewSchema, SiteGroupSchema } from '@sophocles/features/siteGroups/schema';
import { normalize } from 'normalizr';

const siteGroupCurfewAdapter = createEntityAdapter();
const siteGroupAdapter = createEntityAdapter();
const weatherConfigurationAdapter = createEntityAdapter();

const siteGroupSlice = createSlice({
  name: 'siteGroups',
  initialState: {
    siteGroups: siteGroupAdapter.getInitialState({}),
    curfews: siteGroupCurfewAdapter.getInitialState({}),
    weatherConfigurations: weatherConfigurationAdapter.getInitialState({}),
    currentSiteGroupId: null,
    lastLoaded: null,
    view: {
      search: null,
      page: 0,
      order: 'asc',
      orderBy: 'id',
    },
  },
  reducers: {
    setPage(state, action) {
      state.view.page = action.payload;
    },
    setSearch(state, action) {
      state.view.search = action.payload;
    },
    setOrder(state, action) {
      state.view.order = action.payload;
    },
    setOrderBy(state, action) {
      state.view.orderBy = action.payload;
    },
    setCurrentSiteGroupId(state, action) {
      state.currentSiteGroupId = action.payload;
    },
    setLastLoaded(state, action) {
      state.lastLoaded = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadSiteGroupCurfew.fulfilled, (state, action) => {
        if (action.payload === null) return;
        const { entities, result } = normalize(action.payload, SiteGroupCurfewSchema);
        if (entities?.curfews?.[result]) {
          siteGroupCurfewAdapter.upsertOne(state.curfews, entities.curfews[result]);
        }
      })
      .addCase(loadSiteGroupCurfewForSiteGroup.fulfilled, (state, action) => {
        if (action.payload === null) return;
        const { entities, result } = normalize(action.payload, SiteGroupCurfewSchema);
        if (entities?.curfews?.[result]) {
          siteGroupCurfewAdapter.upsertOne(state.curfews, entities.curfews[result]);
        }
      })
      .addCase(loadSiteGroupCurfews.fulfilled, (state, action) => {
        const { entities } = normalize(action.payload.results, [SiteGroupCurfewSchema]);
        siteGroupCurfewAdapter.upsertMany(state, entities.curfews ?? {});
      })
      .addCase(editSiteGroupCurfew.fulfilled, (state, action) => {
        if (action.payload === null) return;
        const { entities, result } = normalize(action.payload, SiteGroupCurfewSchema);
        if (entities?.curfews?.[result]) {
          siteGroupCurfewAdapter.upsertOne(state.curfews, entities.curfews[result]);
        }
      })
      .addCase(loadSiteGroup.fulfilled, (state, action) => {
        if (action.payload === null) return;
        if (action.payload) {
          siteGroupAdapter.upsertOne(state.siteGroups, action.payload);
          state.lastLoaded = new Date().toISOString();
        }
      })
      .addCase(loadSiteGroupWeatherConfigurationForSiteGroup.fulfilled, (state, action) => {
        if (action.payload === null) return;
        weatherConfigurationAdapter.upsertMany(state.weatherConfigurations, action.payload);
      });
  },
});

export const selectSiteGroupCurfewEntities = state => {
  return state.siteGroups.curfews.entities;
};

export const selectSiteGroupEntities = state => {
  return state.siteGroups.siteGroups.entities;
};

export const selectSiteGroupWeatherConfigurationEntities = state => {
  return state.siteGroups.weatherConfigurations.entities;
};

// Site Group Weather Configuration Selectors

export const { selectById: selectSiteGroupWeatherConfigurationById } = weatherConfigurationAdapter.getSelectors(
  selectSiteGroupWeatherConfigurationEntities,
);

// Site Group Curfew Selectors

export const selectSiteGroupCurfewBySiteGroupId = siteGroupId =>
  createSelector(selectSiteGroupCurfewEntities, curfews =>
    Object.values(curfews).find(curfew => curfew.site_group == siteGroupId),
  );

export const { selectById: selectSiteGroupCurfewById } =
  siteGroupCurfewAdapter.getSelectors(selectSiteGroupCurfewEntities);

// Site Group Selectors

export const { selectById: selectSiteGroupById, selectAll: selectAllSiteGroups } =
  siteGroupAdapter.getSelectors(selectSiteGroupEntities);

export const selectDenormalizedSiteGroupById = createSelector(
  selectSiteGroupById,
  selectSiteGroupCurfewEntities,
  selectSiteGroupWeatherConfigurationEntities,
  (siteGroup, curfews, weatherConfigurations) => {
    if (!siteGroup) {
      return null;
    }
    return {
      ...siteGroup,
      curfew: curfews[siteGroup.curfew],
      weatherConfiguration: weatherConfigurations[siteGroup.weather_configuration],
    };
  },
);

export const selectCurrentSiteGroupId = state => {
  return state.siteGroups.currentSiteGroupId;
};

export const selectCurrentSiteGroup = state => {
  return selectDenormalizedSiteGroupById(state, selectCurrentSiteGroupId(state));
};

export const selectSiteGroupLastLoaded = state => {
  return state.siteGroups.lastLoaded;
};

export const {
  setCurrentSiteGroupId,
  setSiteGroupLastLoaded: setLastLoaded,
  setSiteGroupPage: setPage,
  setSiteGroupSearch: setSearch,
  setSiteGroupOrder: setOrder,
} = siteGroupSlice.actions;

const { reducer } = siteGroupSlice;
export default reducer;
